import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { notification } from 'antd';
import {
  SendOutlined, LockOutlined, CloseOutlined, DownloadOutlined,
  LoadingOutlined, CheckCircleFilled, CloseCircleFilled, RightOutlined,
  PlusOutlined, RocketOutlined, UploadOutlined, InboxOutlined,
  FilePdfOutlined, FilePptOutlined, FileWordOutlined, FileExcelOutlined, FileTextOutlined,
  PictureOutlined, ExclamationCircleFilled, ReloadOutlined, ReadOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import VoiceInputButton from '../../components/VoiceInputButton';
import PptResultPanel from './PptResultPanel';
import {
  DEPTS, DeptConf, CAPABILITIES, CapabilityKey, SCENES, DeptCategory,
} from './office-config';
import { useOfficeEngine, OfficeAgent, OfficeTask, WorkspaceProject } from './useOfficeEngine';
import {
  OFFICE_ATTACHMENT_ACCEPT, OFFICE_ATTACHMENT_LIMIT, OfficeAttachment,
} from './useOfficeAttachments';
import {
  generatePdfFromTask, generatePdfFromText, generateWordFromTask, generateWordFromText,
  GeneratedCollabFile,
} from '../../services/teamCollabApi';

/* ============================================================
   v8 共享层：所有呈现模式共用的指挥台/任务坞/汇报抽屉/项目弹窗/气泡
   ============================================================ */

export const deptOf = (id: string) => DEPTS.find((d) => d.id === id)!;

/* ---------------- 成果智能高亮（纯展示端增强，不动任何智能体/后台）
   告警故障=红 · 正常恢复=绿 · 数字指标=金 · 日期时间=蓝 ---------------- */
const HL_RULES: Array<{ re: RegExp; cls: string }> = [
  { re: /(故障|告警|报警|异常|离线|断线|断电|超时|失败|错误|紧急|严重|重大|事故|拥堵|瘫痪|中断|风险|隐患|投诉|未处理|待处理|未解决|逾期|下降|降幅)/g, cls: 'bad' },
  { re: /(已恢复|恢复正常|运行正常|正常|在线|已处理|已解决|已完成|已闭环|通过|良好|稳定|无异常|上升|增长)/g, cls: 'ok' },
  { re: /(\d+(?:\.\d+)?\s*(?:%|％|万元|万辆|万人|万|亿|元|条|次|起|台|套|路|个|项|件|辆|人次|人|车次|小时|分钟|秒|天|km\/h|km|公里|米))/g, cls: 'num' },
  { re: /(\d{4}[-/年]\d{1,2}[-/月]\d{1,2}日?(?:\s*\d{1,2}[:时]\d{2}分?(?::\d{2})?)?|\d{1,2}[:时]\d{2}分?)/g, cls: 'time' },
];

const highlightText = (text: string): React.ReactNode => {
  const nodes: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest) {
    let best: { idx: number; len: number; cls: string; hit: string } | null = null;
    for (const r of HL_RULES) {
      r.re.lastIndex = 0;
      const m = r.re.exec(rest);
      if (m && (best === null || m.index < best.idx)) best = { idx: m.index, len: m[0].length, cls: r.cls, hit: m[0] };
    }
    if (!best) { nodes.push(rest); break; }
    if (best.idx > 0) nodes.push(rest.slice(0, best.idx));
    nodes.push(<i key={key++} className={`aco29-hl ${best.cls}`}>{best.hit}</i>);
    rest = rest.slice(best.idx + best.len);
  }
  return nodes.length === 1 ? nodes[0] : nodes;
};

const SKIP_HL = new Set(['code', 'pre', 'a', 'i', 'img', 'iframe']);

const deepHighlight = (children: React.ReactNode): React.ReactNode =>
  React.Children.map(children, (child) => {
    if (typeof child === 'string') return highlightText(child);
    if (React.isValidElement(child)) {
      const type = typeof child.type === 'string' ? child.type : '';
      if (SKIP_HL.has(type) || String((child.props as any)?.className || '').includes('aco29-hl')) return child;
      const kids = (child.props as any)?.children;
      if (kids) return React.cloneElement(child as React.ReactElement, undefined, deepHighlight(kids));
    }
    return child;
  });

const hl = (Tag: string) => ({ children }: { children?: React.ReactNode }) =>
  React.createElement(Tag, undefined, deepHighlight(children));

/** ReactMarkdown components：正文各层级全部过一遍智能高亮 */
export const HL_MD_COMPONENTS = {
  p: hl('p'), li: hl('li'), td: hl('td'), th: hl('th'),
  h1: hl('h1'), h2: hl('h2'), h3: hl('h3'), h4: hl('h4'),
  strong: hl('strong'), em: hl('em'), blockquote: hl('blockquote'),
} as any;

/* ---------------- 对话气泡（各模式的"人物开口说话"） ---------------- */
export const SpeechBubble: React.FC<{
  text: string;
  accent: string;
  tone?: 'normal' | 'ok' | 'bad';
  className?: string;
}> = ({ text, accent, tone = 'normal', className = '' }) => (
  <div className={`aco8-bubble ${tone} ${className}`} style={{ '--acc': accent } as React.CSSProperties}>
    <span className="tx">{text}</span>
    <i className="tail" />
  </div>
);

/* ---------------- 指挥台（底部输入 + @部门 + 能力扇面） ---------------- */
export interface ConsoleProps {
  selDept: DeptConf | null;
  selAgent: OfficeAgent | null;
  agents: OfficeAgent[];
  selProject: WorkspaceProject | null;
  cmd: string;
  setCmd: (v: string) => void;
  capKey: CapabilityKey;
  setCapKey: (v: CapabilityKey) => void;
  docFmt: 'word' | 'pdf';
  setDocFmt: (v: 'word' | 'pdf') => void;
  mentionOpen: boolean;
  setMentionOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  dispatching: boolean;
  canSend: boolean;
  attachments: OfficeAttachment[];
  attachmentUploading: boolean;
  onAddAttachments: (files: FileList | File[]) => void;
  onRemoveAttachment: (id: string) => void;
  onRetryAttachment: (id: string) => void;
  onSend: () => void;
  onChooseDept: (deptId: string) => void;
  onChooseAgent: (a: OfficeAgent) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  consoleRef: React.RefObject<HTMLDivElement>;
}

const officeAttachmentIcon = (attachment: OfficeAttachment) => {
  const ext = attachment.name.split('.').pop()?.toLowerCase();
  if (attachment.previewUrl) return <img src={attachment.previewUrl} alt="" />;
  if (ext === 'pdf') return <FilePdfOutlined className="pdf" />;
  if (ext === 'doc' || ext === 'docx') return <FileWordOutlined className="word" />;
  if (ext === 'xls' || ext === 'xlsx') return <FileExcelOutlined className="excel" />;
  if (ext === 'ppt' || ext === 'pptx') return <FilePptOutlined className="ppt" />;
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'].includes(ext || '')) return <PictureOutlined className="image" />;
  return <FileTextOutlined className="text" />;
};

export const CommandConsole: React.FC<ConsoleProps> = (p) => {
  const {
    selDept, selAgent, agents, selProject, cmd, setCmd, capKey, setCapKey, docFmt, setDocFmt,
    mentionOpen, setMentionOpen, dispatching, canSend, attachments, attachmentUploading,
    onAddAttachments, onRemoveAttachment, onRetryAttachment,
    onSend, onChooseDept, onChooseAgent, inputRef, consoleRef,
  } = p;
  const zones: DeptCategory[] = ['internal', 'external'];
  const showArtifactFan = selAgent?.kind === 'general';
  // 未选部门时也必须能先确定交付类型；只有明确切到专用智能体时才隐藏通用产物选择。
  const showCapabilityPicker = !selAgent || selAgent.kind === 'general';

  /* 当前选择的任务类型 → 醒目标识（让领导一眼知道这一单是文档/PPT/设计图…） */
  const capBadge = (() => {
    if (!selDept || !selAgent) return null;
    if (selAgent.kind === 'devteam') return { text: `🚀 软件开发${selProject ? ` · ${selProject.name}` : ''}`, tone: 'green' };
    if (selAgent.kind === 'cabin') return { text: `🤖 ${selAgent.name}`, tone: 'violet' };
    const cap = CAPABILITIES.find((c) => c.key === capKey);
    if (!cap) return null;
    const label = cap.key === 'doc' ? `文档编写 · ${docFmt === 'word' ? 'Word' : 'PDF'}` : cap.label;
    return { text: `${cap.glyph} ${label}`, tone: cap.key === 'ppt' || cap.key === 'design' ? 'warm' : '' };
  })();
  /* 鼠标点击输入框 → 只把"输入框本身"弹出为半透明大输入浮层（不改动任何控件布局）；
     程序性 focus（如选部门后自动聚焦）不触发弹出 */
  const [big, setBig] = useState(false);
  /* 浮层内敲 @ → 部门选单直接跟在光标后面弹（不再用底部那个大选单） */
  const popTaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  const [popMention, setPopMention] = useState<{ top: number; left: number } | null>(null);

  /* 浮层打开后，第一次点击普通空白区域只负责收起浮层，不继续触发大厅的“清空部门”等行为。
     点击真实交互控件（button/a/input/select/role=button）则保持浮层，由该控件正常执行。 */
  useEffect(() => {
    if (!big) return undefined;
    const handleOutsidePointer = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.closest('.aco20-pop')) return;
      if (target.closest('button, a, input, textarea, select, [contenteditable="true"], [role="button"], [data-aco-keep-pop]')) return;
      event.stopPropagation();
      setBig(false);
      setPopMention(null);
    };
    document.addEventListener('pointerdown', handleOutsidePointer, true);
    return () => document.removeEventListener('pointerdown', handleOutsidePointer, true);
  }, [big]);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboard = e.clipboardData;
    if (!clipboard) {
      notification.warning({
        message: '未读取到剪贴板内容',
        description: '浏览器没有提供剪贴板数据，请改用“附件”按钮或把文件拖入输入框。',
        placement: 'topRight', duration: 8,
      });
      return;
    }
    const files: File[] = [];
    const clipboardItems = Array.from(clipboard.items || []);
    const source = clipboard.files?.length
      ? Array.from(clipboard.files)
      : clipboardItems.filter((item) => item.kind === 'file').map((item) => item.getAsFile()).filter(Boolean) as File[];
    source.forEach((file, index) => {
      const genericImageName = /^image\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name || '');
      const hasExtension = /\.[a-z0-9]{1,8}$/i.test(file.name || '');
      if (!file.name || !hasExtension || genericImageName) {
        const mimeExtensions: Record<string, string> = {
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
          'application/vnd.ms-powerpoint': 'ppt',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'application/pdf': 'pdf',
        };
        const ext = mimeExtensions[file.type] || file.type.split('/')[1]?.replace('jpeg', 'jpg') || 'bin';
        const kind = file.type.startsWith('image/') ? '粘贴图片' : '粘贴附件';
        files.push(new File([file], `${kind}-${Date.now()}-${index + 1}.${ext}`, { type: file.type, lastModified: Date.now() }));
      } else files.push(file);
    });
    if (!files.length) {
      const plainText = clipboard.getData('text/plain').trim();
      const reportsFile = Array.from(clipboard.types || []).some((type) => type.toLowerCase() === 'files')
        || clipboardItems.some((item) => item.kind === 'file');
      const looksLikeFile = plainText.length < 2048
        && /(?:^|[\\/\s])[^\\/\r\n]+\.(?:pptx?|pdf|docx?|xlsx?|txt|md|png|jpe?g|webp|gif|bmp)$/i.test(plainText);
      if (reportsFile || looksLikeFile) {
        e.preventDefault();
        setBig(true);
        notification.warning({
          message: '没有取得文件内容',
          description: '浏览器只提供了文件名或路径，未提供实际文件。请直接拖入文件，或点击“附件”按钮选择。',
          placement: 'topRight', duration: 8,
        });
      }
      return; // 真正的纯文本照常粘贴
    }
    e.preventDefault();
    onAddAttachments(files);
    setBig(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (!dragDepthRef.current) setDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      onAddAttachments(e.dataTransfer.files);
      setBig(true);
    } else {
      notification.warning({
        message: '没有读取到拖入的文件',
        description: '请从文件管理器拖入本地文件；网页链接或文件快捷方式无法作为附件读取。',
        placement: 'topRight', duration: 8,
      });
    }
  };

  const attachmentList = (large = false) => (
    <div className={`aco-attachments ${large ? 'large' : 'compact'}`} aria-live="polite">
      {attachments.map((attachment) => (
        <div key={attachment.id} className={`aco-attachment ${attachment.error ? 'error' : attachment.uploading ? 'uploading' : 'ready'}`}>
          <span className="aco-attachment-icon">
            {attachment.uploading ? <LoadingOutlined spin /> : attachment.error ? <ExclamationCircleFilled /> : officeAttachmentIcon(attachment)}
          </span>
          <span className="aco-attachment-copy">
            <b title={attachment.name}>{attachment.name}</b>
            <em>{attachment.uploading
              ? (attachment.progressMsg || '正在解析…')
              : attachment.error
                ? attachment.error
                : `${(attachment.charCount || attachment.content.length).toLocaleString()} 字${attachment.tokenEstimate ? ` · 约 ${attachment.tokenEstimate.toLocaleString()} tokens` : ''}`}</em>
          </span>
          {attachment.error && (
            <button className="aco-attachment-retry" title="重新解析" onClick={() => onRetryAttachment(attachment.id)}><ReloadOutlined /></button>
          )}
          <button className="aco-attachment-remove" title={attachment.uploading ? '从本次交办中移除' : '移除附件'} onClick={() => onRemoveAttachment(attachment.id)}><CloseOutlined /></button>
        </div>
      ))}
    </div>
  );

  /** 镜像法测光标在 textarea 里的像素位置 */
  const measureCaret = (ta: HTMLTextAreaElement) => {
    const div = document.createElement('div');
    const st = window.getComputedStyle(ta);
    ['font-size', 'font-family', 'font-weight', 'line-height', 'letter-spacing',
      'padding-top', 'padding-left', 'padding-right', 'box-sizing'].forEach((prop) => {
      div.style.setProperty(prop, st.getPropertyValue(prop));
    });
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordBreak = 'break-word';
    div.style.width = `${ta.clientWidth}px`;
    div.textContent = ta.value.slice(0, ta.selectionStart ?? ta.value.length);
    const mark = document.createElement('span');
    mark.textContent = '​';
    div.appendChild(mark);
    document.body.appendChild(div);
    const at = { top: mark.offsetTop - ta.scrollTop, left: mark.offsetLeft };
    document.body.removeChild(div);
    return at;
  };

  const doSend = () => {
    onSend();
    setBig(false);
    setPopMention(null);
    inputRef.current?.blur();
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === '@' || (e.key === '2' && e.shiftKey)) {
      if (big) {
        /* 等 @ 字符真正落进文本后再量光标位置 */
        setTimeout(() => {
          const ta = popTaRef.current;
          if (!ta) return;
          const c = measureCaret(ta);
          setPopMention({
            top: ta.offsetTop + c.top + 30,
            left: Math.max(14, Math.min(ta.offsetLeft + c.left, ta.clientWidth - 250)),
          });
        }, 0);
      } else {
        setMentionOpen(true);
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) doSend();
    }
    if (e.key === 'Escape') {
      if (popMention) { setPopMention(null); return; }
      setMentionOpen(false);
      setBig(false);
    }
  };

  return (
    <div
      className={`aco3-console ${dragOver ? 'is-dragging-file' : ''}`}
      ref={consoleRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={OFFICE_ATTACHMENT_ACCEPT}
        onChange={(e) => {
          if (e.target.files?.length) {
            onAddAttachments(e.target.files);
            setBig(true);
          }
          e.target.value = '';
        }}
      />
      {dragOver && !big && (
        <div className="aco-attachment-drop-mask">
          <InboxOutlined />
          <b>松开即可引用附件</b>
          <span>自动解析文档；PDF 与图片将进行 OCR 识别</span>
        </div>
      )}
      {mentionOpen && (
        <div className="aco3-mention" onClick={(e) => e.stopPropagation()}>
          {zones.map((cat) => (
            <div key={cat}>
              <div className="aco3-mention-h" style={{ color: SCENES[cat].tint }}>
                {SCENES[cat].glyph} {SCENES[cat].label}
              </div>
              {DEPTS.filter((d) => d.category === cat).map((d) => (
                <button key={d.id} className="aco3-mention-item" onClick={() => onChooseDept(d.id)}>
                  <i style={{ background: d.accent }} />
                  <b>{d.name}</b>
                  <span>{d.sub}</span>
                  <RightOutlined className="arr" />
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {showArtifactFan && !big && (
        <div className="aco3-fan" onClick={(e) => e.stopPropagation()}>
          {CAPABILITIES.map((t, i) => (
            <div key={t.key} className="aco3-fan-slot">
              {t.key === 'doc' && capKey === 'doc' && (
                <div className="aco3-fan-sub">
                  <button className={docFmt === 'word' ? 'on' : ''} onClick={() => setDocFmt('word')}>Word</button>
                  <button className={docFmt === 'pdf' ? 'on' : ''} onClick={() => setDocFmt('pdf')}>PDF</button>
                </div>
              )}
              <button
                className={`aco3-fan-card ${capKey === t.key ? 'on' : ''}`}
                style={{ '--fi': String(i - (CAPABILITIES.length - 1) / 2) } as React.CSSProperties}
                onClick={() => setCapKey(t.key)}
              >
                <span className="g">{t.glyph}</span>
                <b>{t.label}</b>
                <em>{t.key === 'doc' && capKey === 'doc' ? (docFmt === 'word' ? '.docx' : '.pdf') : t.hint}</em>
              </button>
            </div>
          ))}
        </div>
      )}

      {capBadge && <span className={`aco28-captag aco28-cap-float ${capBadge.tone}`}>{capBadge.text}</span>}

      <div className="aco3-console-in" onClick={(e) => e.stopPropagation()}>
        <button
          className={`aco3-at ${selDept ? 'on' : ''}`}
          style={selDept ? ({ '--acc': selDept.accent } as React.CSSProperties) : undefined}
          onClick={(e) => { e.stopPropagation(); setMentionOpen((v) => !v); }}
        >
          <span className="sym">@</span>
          {selDept ? <b>{selDept.name}</b> : <span className="ph">部门</span>}
        </button>

        {selDept && (
          <div className="aco3-agent-strip">
            {agents.map((a) => (
              <button
                key={a.id}
                className={`aco3-agent-chip ${selAgent?.id === a.id ? 'on' : ''} ${!a.available ? 'dis' : ''}`}
                style={{ '--acc': selDept.accent } as React.CSSProperties}
                disabled={!a.available}
                title={a.desc}
                onClick={() => onChooseAgent(a)}
              >
                {!a.available && <LockOutlined className="lk" />}
                {a.kind === 'devteam' && <RocketOutlined className="rk" />}
                {a.name}
                {a.kind === 'devteam' && selAgent?.id === a.id && selProject && <em>· {selProject.name}</em>}
                {!a.available && <span className="off">待接入</span>}
              </button>
            ))}
          </div>
        )}

        {!!attachments.length && !big && attachmentList(false)}

        <button
          className={`aco-attach-trigger ${attachments.length ? 'has-files' : ''}`}
          type="button"
          title={attachments.length >= OFFICE_ATTACHMENT_LIMIT ? `最多引用 ${OFFICE_ATTACHMENT_LIMIT} 个附件` : '引用附件，也可拖入或粘贴'}
          aria-label="引用附件"
          disabled={dispatching || attachments.length >= OFFICE_ATTACHMENT_LIMIT}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadOutlined />
          <span>附件</span>
          {!!attachments.length && <i>{attachments.length}</i>}
        </button>

        <textarea
          ref={inputRef}
          className="aco3-input"
          rows={1}
          value={cmd}
          onMouseDown={(e) => { e.preventDefault(); setBig(true); }}
          onChange={(e) => setCmd(e.target.value)}
          onKeyDown={onInputKey}
          onPaste={handlePaste}
          placeholder={selDept
            ? (selAgent?.kind === 'devteam'
              ? `向数字人团队提开发需求${selProject ? `（项目：${selProject.name}）` : '（先选择项目）'}…`
              : `告诉 ${selDept.name} 要干什么，也可拖入或粘贴附件…`)
            : '输入 @ 或点击部门，支持拖入或粘贴附件…'}
        />

        <button className="aco3-send" disabled={!canSend} onClick={doSend} style={selDept ? ({ '--acc': selDept.accent } as React.CSSProperties) : undefined}>
          {dispatching || attachmentUploading ? <LoadingOutlined /> : <SendOutlined />}
          <span>交办</span>
        </button>
      </div>

      {/* 半透明大输入浮层：只弹输入框本身，原布局纹丝不动 */}
      {big && (
        <div
          className="aco20-pop"
          style={selDept ? ({ '--acc': selDept.accent } as React.CSSProperties) : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {dragOver && (
            <div className="aco-attachment-drop-mask">
              <InboxOutlined />
              <b>松开即可引用附件</b>
              <span>自动解析文档；PDF 与图片将进行 OCR 识别</span>
            </div>
          )}
          <div className="hd">
            <b>{selDept ? `对 ${selDept.name} 下达指令` : '下达指令'}</b>
            <span>Enter 交办 · Shift+Enter 换行 · Esc 收起</span>
            <button className="mini" title="最小化" onClick={() => setBig(false)}>—</button>
          </div>
          {showCapabilityPicker && (
            <div className="aco20-type-picker" aria-label="选择交付类型">
              <span className="label">交付类型</span>
              <div className="options">
                {CAPABILITIES.map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    className={capKey === type.key ? 'on' : ''}
                    aria-pressed={capKey === type.key}
                    onClick={() => setCapKey(type.key)}
                  >
                    <i>{type.glyph}</i><b>{type.label}</b>
                  </button>
                ))}
              </div>
              {capKey === 'doc' && (
                <div className="docfmt" aria-label="选择文档格式">
                  <button type="button" className={docFmt === 'word' ? 'on' : ''} aria-pressed={docFmt === 'word'} onClick={() => setDocFmt('word')}>Word</button>
                  <button type="button" className={docFmt === 'pdf' ? 'on' : ''} aria-pressed={docFmt === 'pdf'} onClick={() => setDocFmt('pdf')}>PDF</button>
                </div>
              )}
            </div>
          )}
          <textarea
            ref={popTaRef}
            autoFocus
            value={cmd}
            onChange={(e) => setCmd(e.target.value)}
            onKeyDown={onInputKey}
            onPaste={handlePaste}
            placeholder={selDept
              ? (selAgent?.kind === 'devteam'
                ? `向数字人团队提开发需求${selProject ? `（项目：${selProject.name}）` : '（先选择项目）'}…`
                : `告诉 ${selDept.name} 要干什么；文件可拖入这里，截图可直接粘贴…`)
              : '先 @ 一个部门；也可先拖入文件或直接粘贴截图…'}
          />
          <div className="aco20-attachment-zone">
            {!!attachments.length && attachmentList(true)}
            <button
              className="aco20-add-attachment"
              type="button"
              disabled={dispatching || attachments.length >= OFFICE_ATTACHMENT_LIMIT}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadOutlined />
              {attachments.length ? `继续添加（${attachments.length}/${OFFICE_ATTACHMENT_LIMIT}）` : '添加附件'}
            </button>
            <span>支持 Word / Excel / PPT / PDF / 图片 · 单个 ≤ 100MB</span>
          </div>
          <div className="ft">
            <div className="voice">
              <VoiceInputButton value={cmd} onChange={setCmd} disabled={dispatching} size="normal" className="aco27-voice big" title="点一下开始说话，再点一下结束" />
              <em>懒得打字就点麦克风直接说，说完文字自动落进输入框</em>
            </div>
            <button className="send" disabled={!canSend} onClick={doSend}>
              {dispatching || attachmentUploading ? <LoadingOutlined /> : <SendOutlined />}
              {attachmentUploading ? '等待附件识别' : '交办'}
            </button>
          </div>
          {/* @ 光标处的部门快捷选单 */}
          {popMention && (
            <div className="aco20-mention" style={{ top: popMention.top, left: popMention.left }} onClick={(e) => e.stopPropagation()}>
              {DEPTS.map((d) => (
                <button
                  key={d.id}
                  onClick={() => {
                    onChooseDept(d.id);
                    setPopMention(null);
                    setTimeout(() => popTaRef.current?.focus(), 120);
                  }}
                >
                  <i style={{ background: d.accent }} />
                  <b>{d.name}</b>
                  <em>{d.sub}</em>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------------- 任务坞（常驻 + 可最小化，记忆状态） ---------------- */
export const TaskDock: React.FC<{ tasks: OfficeTask[]; onOpen: (id: string) => void }> = ({ tasks, onOpen }) => {
  const [min, setMin] = useState(() => localStorage.getItem('aco.dock.min') === '1');
  useEffect(() => { localStorage.setItem('aco.dock.min', min ? '1' : '0'); }, [min]);
  const runningN = tasks.filter((t) => t.status === 'running' || t.status === 'dispatching').length;

  if (min) {
    return (
      <button className="aco3-dock-mini" onClick={(e) => { e.stopPropagation(); setMin(false); }} title="展开任务动态">
        ▤ 任务动态{runningN > 0 && <em>{runningN}</em>}
      </button>
    );
  }
  return (
    <div className="aco3-dock" onClick={(e) => e.stopPropagation()}>
      <div className="aco3-dock-h">
        任务动态<em>{runningN} 进行中</em>
        <button className="aco3-dock-minbtn" title="最小化" onClick={() => setMin(true)}>—</button>
      </div>
      <div className="aco3-dock-list">
        {tasks.length === 0 && (
          <div className="aco3-dock-empty">今日尚未交办任务<br />@ 一个部门开始吧</div>
        )}
        {tasks.map((t) => {
          const d = deptOf(t.deptId);
          return (
            <button key={t.localId} className={`aco3-dock-card st-${t.status}`} style={{ '--acc': d.accent } as React.CSSProperties} onClick={() => onOpen(t.localId)}>
              <div className="l1">
                <b>{d.name}</b>
                <span className="ag">{t.agentName}</span>
                {t.status === 'running' || t.status === 'dispatching' ? <LoadingOutlined className="ic run" />
                  : t.status === 'completed' ? <CheckCircleFilled className="ic ok" />
                  : <CloseCircleFilled className="ic bad" />}
              </div>
              <div className="l2">{t.command}</div>
              <div className="l3">
                {t.status === 'failed' ? (t.error || '执行失败') : t.stepText}
                {t.resultFileName && <em> · {t.resultFileName}</em>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- 汇报抽屉 ---------------- */
export const ReportDrawer: React.FC<{
  task: OfficeTask;
  onClose: () => void;
  onDownload: () => void;
  /** 打开内部研发办公室（本页只读实况，严禁跳数字员工页面） */
  onEnterDevOffice?: () => void;
}> = ({ task, onClose, onDownload, onEnterDevOffice }) => {
  const d = deptOf(task.deptId);
  const [showThinking, setShowThinking] = useState(false);
  const [exporting, setExporting] = useState<'word' | 'pdf' | null>(null);
  const running = task.status === 'running' || task.status === 'dispatching';
  const isOralResult = task.status === 'completed'
    && !task.resultFileId
    && !task.resultImageUrl
    && task.kind !== 'ppt'
    && task.kind !== 'design'
    && (task.resultType === 'text' || !task.resultType);

  const downloadGeneratedFile = async (generated: GeneratedCollabFile, fallbackExt: 'docx' | 'pdf') => {
    if (!generated.success) throw new Error(generated.error || '文件生成失败');
    const fileId = generated.resultFileId || generated.fileId;
    if (!fileId) throw new Error('文件生成成功，但未返回文件地址');
    const response = await api.get(`/workroom/files/${encodeURIComponent(fileId)}`, { responseType: 'blob', timeout: 120000 });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    const defaultName = `${task.command.slice(0, 50).replace(/[\\/:*?"<>|]/g, '_') || '口头汇报'}.${fallbackExt}`;
    link.href = url;
    link.download = generated.resultFileName || defaultName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportOralResult = async (format: 'word' | 'pdf') => {
    if (exporting) return;
    setExporting(format);
    try {
      const generated = task.serverTaskId
        ? format === 'word'
          ? await generateWordFromTask(task.serverTaskId)
          : await generatePdfFromTask(task.serverTaskId)
        : format === 'word'
          ? await generateWordFromText(task.command, task.result || '')
          : await generatePdfFromText(task.command, task.result || '');
      await downloadGeneratedFile(generated, format === 'word' ? 'docx' : 'pdf');
      notification.success({ message: `${format === 'word' ? 'Word' : 'PDF'} 已导出`, placement: 'topRight' });
    } catch (error: any) {
      notification.error({
        message: `${format === 'word' ? 'Word' : 'PDF'} 导出失败`,
        description: error?.response?.data?.message || error?.message || '请稍后重试',
        placement: 'topRight',
      });
    } finally {
      setExporting(null);
    }
  };
  return (
    <>
      <div className="aco3-mask" onClick={onClose} />
      <div className="aco3-drawer" style={{ '--acc': d.accent } as React.CSSProperties} onClick={(e) => e.stopPropagation()}>
        <div className="aco3-drawer-h">
          <span className="tag">{d.name}</span>
          <b>{task.agentName}{task.projectName ? ` · ${task.projectName}` : ''}</b>
          <button className="x" onClick={onClose}><CloseOutlined /></button>
        </div>
        <div className="aco3-drawer-cmd">「{task.command}」</div>

        {/* AI 参考的知识（文档知识库 + 网络情报，检索后即存，成功/失败/刷新都可见） */}
        {!!task.matchedDocs?.length && (() => {
          const docKb = task.matchedDocs.filter((x) => (x.source || 'doc') === 'doc');
          const intel = task.matchedDocs.filter((x) => x.source === 'intel');
          const grp = (title: string, cls: string, list: typeof task.matchedDocs) => list.length ? (
            <div className={`grp ${cls}`}>
              <div className="gh">{title} <em>{list.length}</em></div>
              {list.map((doc, i) => (
                <div key={`${cls}-${doc.name}-${i}`} className="kr">
                  <i className="rk">{i + 1}</i>
                  <FileTextOutlined className="fi" />
                  <b>{doc.name}</b>
                  {typeof doc.score === 'number' && <em>{(doc.score <= 1 ? doc.score * 100 : doc.score).toFixed(0)}%</em>}
                </div>
              ))}
            </div>
          ) : null;
          return (
            <div className="aco40-kb">
              <div className="kh"><ReadOutlined /> AI 参考的知识 <em>{task.matchedDocs.length}</em></div>
              <div className="kl">
                {grp('📚 文档知识库', 'doc', docKb)}
                {grp('🌐 网络情报', 'intel', intel)}
              </div>
            </div>
          );
        })()}

        {running && (
          <div className="aco3-drawer-run">
            <LoadingOutlined /> {task.stepText}
            {task.thinking && (
              <>
                <button className="think-t" onClick={() => setShowThinking((v) => !v)}>{showThinking ? '收起思考过程' : '查看思考过程'}</button>
                {showThinking && <pre className="think">{task.thinking}</pre>}
              </>
            )}
          </div>
        )}

        {task.status === 'failed' && <div className="aco3-drawer-err">{task.error || '执行失败'}</div>}

        {task.status === 'completed' && (
          <div className="aco3-drawer-body">
            {/* 设计图成果：画框式大图展示 */}
            {task.resultImageUrl && (
              <div className="aco21-designview">
                <img className="aco3-design-img" src={task.resultImageUrl} alt="设计图" />
                <div className="bar">
                  <span className="cap">🎨 AI 设计图 · 已完成</span>
                  <a href={task.resultImageUrl} target="_blank" rel="noreferrer">🔍 查看原图</a>
                  <a href={task.resultImageUrl} download>⬇ 下载</a>
                </div>
              </div>
            )}
            {/* PPT 成果：逐页成片 + 单页修改意见重生成 + 导出 */}
            {task.kind === 'ppt' && task.pptProjectId && (
              <PptResultPanel pptProjectId={task.pptProjectId} title={task.command.slice(0, 30)} />
            )}
            {!task.resultImageUrl && (task.resultKind === 'html' && /<\w+[^>]*>/.test(task.result || '')
              ? <iframe className="aco3-html" sandbox="allow-same-origin" srcDoc={task.result || ''} title="智能体结果" />
              : <div className="aco3-md aco29-rich"><ReactMarkdown remarkPlugins={[remarkGfm]} components={HL_MD_COMPONENTS}>{task.result || ''}</ReactMarkdown></div>)}
          </div>
        )}

        <div className="aco3-drawer-foot">
          {isOralResult && (
            <>
              <button className="dl" disabled={!!exporting} onClick={() => void exportOralResult('word')}>
                {exporting === 'word' ? <LoadingOutlined /> : <FileWordOutlined />} 导出 Word
              </button>
              <button className="dl" disabled={!!exporting} onClick={() => void exportOralResult('pdf')}>
                {exporting === 'pdf' ? <LoadingOutlined /> : <FilePdfOutlined />} 导出 PDF
              </button>
            </>
          )}
          {task.resultFileId && (
            <button className="dl" onClick={onDownload}><DownloadOutlined /> 下载 {task.resultFileName || '产物文件'}</button>
          )}
          {task.kind === 'devteam' && task.status === 'completed' && onEnterDevOffice && (
            <button className="dl" onClick={() => { onClose(); onEnterDevOffice(); }}><RocketOutlined /> 进入研发办公室查看进展</button>
          )}
          {task.kind === 'ppt' && task.pptProjectId && (
            <button className="dl" onClick={() => { window.location.href = `/ppt-designer/${task.pptProjectId}`; }}><RocketOutlined /> 进入 PPT 工作室出片</button>
          )}
          {task.kind === 'design' && task.resultImageUrl && (
            <a className="dl" href={task.resultImageUrl} download target="_blank" rel="noreferrer"><DownloadOutlined /> 下载设计图</a>
          )}
          {task.finishedAt && <em>用时 {Math.max(1, Math.round(((task.finishedAt || 0) - task.startedAt) / 1000))} 秒</em>}
        </div>
      </div>
    </>
  );
};

/* ---------------- 研发进行时 · 在研项目总览（领导视角具象化选项目） ---------------- */
/** 项目舱里的数字员工小队立绘（复用既有机器人素材轮换） */
const POD_BOTS = [
  '/aco-assets/bot-tech-idle.webp',
  '/aco-assets/bot-monitor-idle.webp',
  '/aco-assets/bot-design-idle.webp',
  '/aco-assets/bot-toll-idle.webp',
  '/aco-assets/bot-station-idle.webp',
  '/aco-assets/bot-audit-idle.webp',
];

interface PodSummary {
  activeTasks?: number;
  pendingConfirmations?: number;
  runningDelegates?: number;
  blockedTasks?: number;
  displayStatus?: string;
  displayText?: string;
}

/** 项目舱主题色（轮换，让每张卡有自己的底色与边框，别淹在系统背景里） */
const POD_THEMES = [
  { p1: 'rgba(24, 50, 92, 0.94)', p2: 'rgba(10, 22, 44, 0.96)', bd: '#3b82f6' },
  { p1: 'rgba(13, 60, 55, 0.94)', p2: 'rgba(6, 27, 25, 0.96)', bd: '#14b8a6' },
  { p1: 'rgba(54, 33, 90, 0.94)', p2: 'rgba(23, 13, 42, 0.96)', bd: '#a78bfa' },
  { p1: 'rgba(78, 50, 12, 0.94)', p2: 'rgba(36, 23, 6, 0.96)', bd: '#f59e0b' },
  { p1: 'rgba(16, 60, 33, 0.94)', p2: 'rgba(7, 27, 15, 0.96)', bd: '#34d399' },
  { p1: 'rgba(78, 23, 42, 0.94)', p2: 'rgba(36, 10, 19, 0.96)', bd: '#fb7185' },
];

/** 项目实时态势 → 舱体状态灯 */
const podState = (s: PodSummary | null) => {
  if (!s) return { key: 'load', label: '连线中…' };
  if ((s.pendingConfirmations || 0) > 0) return { key: 'confirm', label: '有事项待您确认' };
  if ((s.activeTasks || 0) + (s.runningDelegates || 0) > 0) return { key: 'run', label: '正在研发' };
  if ((s.blockedTasks || 0) > 0) return { key: 'block', label: '进度受阻' };
  return { key: 'idle', label: '待命 · 可交办' };
};

export const ProjectModal: React.FC<{
  engine: ReturnType<typeof useOfficeEngine>;
  onCancel: () => void;
  onPick: (p: WorkspaceProject) => void;
}> = ({ engine, onCancel, onPick }) => {
  const [projects, setProjects] = useState<WorkspaceProject[] | null>(null);
  const [summaries, setSummaries] = useState<Record<string, PodSummary | null>>({});
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const railRef = useRef<HTMLDivElement | null>(null);
  /* 鼠标左键按住拖动翻阅 */
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ x: number; sl: number } | null>(null);
  const dragMovedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    engine.loadProjects()
      .then(async (list) => {
        if (!alive) return;
        setProjects(list);
        /* 每个项目并行拉实时态势，回来一个亮一个 */
        list.forEach(async (p) => {
          try {
            const { data } = await api.get(`/digital-employees/projects/${p.id}/status-summary`);
            if (alive) setSummaries((prev) => ({ ...prev, [p.id]: data || {} }));
          } catch {
            if (alive) setSummaries((prev) => ({ ...prev, [p.id]: {} }));
          }
        });
      })
      .catch((e) => { setProjects([]); setErr(e?.response?.data?.message || e?.message || '项目列表加载失败'); });
    return () => { alive = false; };
    // 仅挂载时拉取一次；engine 对象每次渲染都是新引用，不能作为依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doCreate = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setErr('');
    try {
      const p = await engine.createProject(name);
      onPick({ id: p.id, name: p.name });
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || '创建项目失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aco17-wrap" onClick={onCancel}>
      <div className="aco17-stage" onClick={(e) => e.stopPropagation()}>
        <header className="aco17-head">
          <div className="tt">
            <b>研发进行时</b>
            <span>技术研发公司 · 当前在研项目一览，左右翻阅，点选一个项目即可交办新需求</span>
          </div>
          <div className="aco17-search">
            <i>🔍</i>
            <input
              value={q}
              placeholder="搜索项目名称…"
              onChange={(e) => setQ(e.target.value)}
            />
            {q && <button className="clr" onClick={() => setQ('')}>✕</button>}
          </div>
          <button className="x" onClick={onCancel}><CloseOutlined /></button>
        </header>
        {err && <div className="aco17-err">{err}</div>}

        <div className="aco17-carousel">
          <button
            className="nav l"
            onClick={(e) => { e.stopPropagation(); railRef.current?.scrollBy({ left: -Math.round(railRef.current.clientWidth * 0.75), behavior: 'smooth' }); }}
          >‹</button>
          <button
            className="nav r"
            onClick={(e) => { e.stopPropagation(); railRef.current?.scrollBy({ left: Math.round(railRef.current.clientWidth * 0.75), behavior: 'smooth' }); }}
          >›</button>
        <div
          className={`aco17-rail ${dragging ? 'dragging' : ''}`}
          ref={railRef}
          onMouseDown={(e) => {
            if (e.button !== 0 || !railRef.current) return;
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            dragRef.current = { x: e.clientX, sl: railRef.current.scrollLeft };
            dragMovedRef.current = false;
            /* ⚠️ 不能在按下瞬间就进入拖动态：dragging 会关掉卡片的
               pointer-events，click 就永远组不成了（点击项目失灵的根因）。
               只有移动超过阈值才算拖动。 */
          }}
          onMouseMove={(e) => {
            const d = dragRef.current;
            if (!d || !railRef.current) return;
            if (e.buttons !== 1) { dragRef.current = null; setDragging(false); return; }
            const dx = e.clientX - d.x;
            if (!dragMovedRef.current && Math.abs(dx) > 6) { dragMovedRef.current = true; setDragging(true); }
            if (dragMovedRef.current) railRef.current.scrollLeft = d.sl - dx;
          }}
          onMouseUp={() => { dragRef.current = null; setDragging(false); }}
          onMouseLeave={() => { dragRef.current = null; setDragging(false); }}
          onClickCapture={(e) => {
            /* 拖动结束后的松手不当作点击选卡 */
            if (dragMovedRef.current) { e.stopPropagation(); e.preventDefault(); dragMovedRef.current = false; }
          }}
        >
          {projects === null && (
            <div className="aco17-loading"><LoadingOutlined /> 正在清点在研项目…</div>
          )}
          {projects !== null && q.trim() && projects.filter((p) => p.name.includes(q.trim())).length === 0 && (
            <div className="aco17-loading">没有名称包含「{q.trim()}」的项目</div>
          )}
          {projects?.filter((p) => !q.trim() || p.name.includes(q.trim())).map((p, pi) => {
            const s = summaries[p.id] ?? null;
            const st = podState(s);
            const team = Math.max(0, p.digitalEmployeeCount || 0);
            const shown = Math.min(team, 5);
            const running = (s?.activeTasks || 0) + (s?.runningDelegates || 0);
            return (
              <button
                key={p.id}
                className={`aco17-pod st-${st.key}`}
                style={{
                  '--pi': String(pi),
                  '--p1': POD_THEMES[pi % POD_THEMES.length].p1,
                  '--p2': POD_THEMES[pi % POD_THEMES.length].p2,
                  '--pbd': POD_THEMES[pi % POD_THEMES.length].bd,
                } as React.CSSProperties}
                onClick={() => onPick(p)}
              >
                <span className="lightbar" />
                <div className="ph">
                  <span className="beacon"><i /></span>
                  <em>{st.label}</em>
                  {running > 0 && <u className="run">{running} 项在办</u>}
                </div>
                <b className="pname">{p.name}</b>
                <div className="team">
                  {shown === 0 && <span className="noteam">团队待组建 · 交办后自动配备</span>}
                  {Array.from({ length: shown }).map((_, k) => (
                    <img key={k} src={POD_BOTS[(pi + k) % POD_BOTS.length]} alt="" draggable={false} style={{ '--bk': String(k) } as React.CSSProperties} />
                  ))}
                  {team > shown && <i className="more">+{team - shown}</i>}
                  {team > 0 && <em className="tc">{team} 名数字员工</em>}
                </div>
                <div className={`live ${st.key === 'run' ? 'on' : ''}`}>
                  {s === null ? <LoadingOutlined /> : <i className="dot" />}
                  <span>{s?.displayText || (s === null ? '正在连线现场…' : '现场安静，随时可开工')}</span>
                </div>
                <div className="mets">
                  <span><b>{s?.activeTasks ?? '—'}</b><em>任务</em></span>
                  <span><b>{s?.runningDelegates ?? '—'}</b><em>执行中</em></span>
                  <span className={s?.blockedTasks ? 'bad' : ''}><b>{s?.blockedTasks ?? '—'}</b><em>受阻</em></span>
                  <span className={s?.pendingConfirmations ? 'warn' : ''}><b>{s?.pendingConfirmations ?? '—'}</b><em>待确认</em></span>
                </div>
                <span className="go"><RocketOutlined /> 选用该项目交办</span>
              </button>
            );
          })}

          {/* 奠基新项目 */}
          {projects !== null && !q.trim() && (
            <div className={`aco17-pod new ${creating ? 'editing' : ''}`} style={{ '--pi': String(projects.length) } as React.CSSProperties}>
              {creating ? (
                <div className="newform">
                  <b>为新项目奠基</b>
                  <input
                    autoFocus
                    value={newName}
                    maxLength={60}
                    placeholder="输入项目名称，工地目录自动生成"
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') doCreate(); if (e.key === 'Escape') setCreating(false); }}
                  />
                  <div className="acts">
                    <button className="ok" disabled={!newName.trim() || busy} onClick={doCreate}>{busy ? <LoadingOutlined /> : '奠基开工'}</button>
                    <button className="no" onClick={() => setCreating(false)}>取消</button>
                  </div>
                </div>
              ) : (
                <button className="newbtn" onClick={() => setCreating(true)}>
                  <i><PlusOutlined /></i>
                  <b>新项目奠基</b>
                  <em>起个名字，数字人团队即刻进场</em>
                </button>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};
